<!--
  StatusIcon — the circle mark for a status value, drawn as inline SVG. Shared by
  every icon+label status field (task_status, review_status, …).

    todo / draft   hollow ring (draft is dashed)        neutral
    doing / open   ring + three dots                    info (blue)
    submitted      ring + up-arrow (sent for review)    warning (amber)
    done/approved  filled disc + white tick             success (green)
    won            filled disc + white tick             success (green)
    dismissed      ring + × (won't do)                  neutral (muted)
    rejected       filled disc + white ×                danger (red)
    lost           filled disc + white ×                danger (red)

  Any other value falls through to a plain neutral ring, so an ad-hoc status
  (e.g. an "all" filter pseudo-value) still renders.

  Color rides `currentColor`, set per status via the classes below, so the same
  glyph renders correctly in menus, cells, and both themes.
-->
<template>
  <svg
    :width="size"
    :height="size"
    :viewBox="`0 0 ${size} ${size}`"
    class="status-icon"
    :class="status"
    aria-hidden="true"
  >
    <!-- Terminal states: filled disc + white mark. -->
    <template v-if="filled">
      <circle :cx="c" :cy="c" :r="r + 0.5" fill="currentColor" />
      <path
        :d="filled === 'check' ? tick : cross"
        fill="none"
        stroke="var(--auger-text-inverted, #fff)"
        :stroke-width="sw + 0.4"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </template>

    <!-- In-progress states: outline ring + optional inner mark. -->
    <template v-else>
      <circle
        :cx="c"
        :cy="c"
        :r="r"
        fill="none"
        stroke="currentColor"
        :stroke-width="sw"
        :stroke-dasharray="status === 'draft' ? dash : undefined"
      />
      <template v-if="status === 'doing' || status === 'open'">
        <circle :cx="c - dotOff" :cy="c" :r="dotR" fill="currentColor" />
        <circle :cx="c" :cy="c" :r="dotR" fill="currentColor" />
        <circle :cx="c + dotOff" :cy="c" :r="dotR" fill="currentColor" />
      </template>
      <path
        v-else-if="status === 'submitted'"
        :d="arrow"
        fill="none"
        stroke="currentColor"
        :stroke-width="sw"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        v-else-if="status === 'dismissed'"
        :d="cross"
        stroke="currentColor"
        :stroke-width="sw"
        stroke-linecap="round"
      />
    </template>
  </svg>
</template>

<script setup lang="ts">
import { computed } from "vue"

const props = withDefaults(
  defineProps<{
    status: string
    size?: number
  }>(),
  { size: 20 }
)

const sw = 1.25
const c = computed(() => props.size / 2)
const r = computed(() => props.size / 2 - 2)
const dotOff = computed(() => r.value * 0.5)
const dotR = computed(() => props.size * 0.075)

// 'check' | 'cross' | null — the terminal (filled) states.
const filled = computed<"check" | "cross" | null>(() => {
  if (props.status === "done" || props.status === "approved" || props.status === "won")
    return "check"
  if (props.status === "rejected" || props.status === "lost") return "cross"
  return null
})

const dash = computed(() => `${(props.size * 0.13).toFixed(1)} ${(props.size * 0.09).toFixed(1)}`)

const tick = computed(() => {
  const s = props.size
  return `M${s * 0.3} ${s * 0.52} L${s * 0.44} ${s * 0.66} L${s * 0.71} ${s * 0.35}`
})
const cross = computed(() => {
  const s = props.size
  const a = s * 0.35
  const b = s * 0.65
  return `M${a} ${a} L${b} ${b} M${b} ${a} L${a} ${b}`
})
const arrow = computed(() => {
  const s = props.size
  const top = s * 0.32
  const bot = s * 0.68
  const w = s * 0.16
  return `M${c.value} ${bot} L${c.value} ${top} M${c.value - w} ${top + w} L${c.value} ${top} L${c.value + w} ${top + w}`
})
</script>

<style>
.status-icon {
  display: block;
  flex-shrink: 0;
}
.status-icon.todo,
.status-icon.draft {
  color: var(--auger-text-weak);
}
.status-icon.doing,
.status-icon.open {
  color: var(--auger-info);
}
.status-icon.submitted {
  color: var(--auger-info);
}
.status-icon.done,
.status-icon.approved,
.status-icon.won {
  color: var(--auger-success);
}
.status-icon.rejected,
.status-icon.lost {
  color: var(--auger-danger);
}
.status-icon.dismissed {
  color: var(--auger-text-weak);
  opacity: 0.7;
}
</style>
